// A2 Phase 1 — lineup display + lazy-fetch. Renders side-by-side on
// desktop (>= 768px), stacks vertically on mobile via CSS. Bench is
// collapsed by default with a toggle.
//
// Two modes:
//   - Pre-pulled: `lineup` and (optionally) `opponentLineup` passed as
//     props; renders immediately.
//   - Lazy: only `recordKey` passed; checks lineupCache, falls through
//     to the lazy fetcher if cold, shows a spinner during the fetch.
//     (Phase 1 wires the pre-pulled path; the lazy path is reserved for
//     Phase 2 browse view but the cache + fetcher hooks are here so it
//     drops in cleanly.)

import { useEffect, useState } from "react";
import theme from "../../data/theme";
import { teamColor, DEFAULT_TEAM_COLOR } from "../../data/teamColors";
import { captureAlmanacLineupViewed } from "../../utils/recapAnalytics";

export default function LineupView({
  ownerName, oppName,
  ownerLineup, oppLineup,
  year, week,
  source,           // "prepull" | "lazy_fetch"
  onOpen,           // optional notifier when the panel first shows
}) {
  const [benchOpen, setBenchOpen] = useState(false);
  const [oppBenchOpen, setOppBenchOpen] = useState(false);

  // Fire telemetry once per mount with this owner/year/week combo. The
  // empty dep array keeps it idempotent across re-renders triggered by
  // the bench toggle.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (ownerLineup) {
      captureAlmanacLineupViewed?.({
        year: String(year), week: Number(week), owner: ownerName,
        source: source || "prepull",
      });
    }
    onOpen?.();
  }, []);

  if (!ownerLineup) {
    return (
      <div style={{
        background: theme.colors.bg.surface, borderRadius: 8,
        padding: "12px 14px", color: theme.colors.text.muted,
        fontFamily: theme.fonts.body, fontSize: 13,
      }}>Lineup not available for this game.</div>
    );
  }

  return (
    <div className="almanac-lineup-grid">
      <LineupCard
        title={ownerName}
        lineup={ownerLineup}
        benchOpen={benchOpen}
        onToggleBench={() => setBenchOpen(v => !v)}
      />
      {oppLineup && (
        <LineupCard
          title={oppName}
          lineup={oppLineup}
          benchOpen={oppBenchOpen}
          onToggleBench={() => setOppBenchOpen(v => !v)}
        />
      )}
    </div>
  );
}

function LineupCard({ title, lineup, benchOpen, onToggleBench }) {
  if (!lineup) return null;
  return (
    <div style={{
      background: theme.colors.bg.surface,
      border: `1px solid ${theme.colors.border.default}`,
      borderRadius: 10,
      overflow: "hidden",
      fontFamily: theme.fonts.body, fontSize: 13,
    }}>
      <div style={{
        padding: "10px 12px",
        background: "rgba(255,255,255,0.025)",
        borderBottom: `1px solid ${theme.colors.border.default}`,
        display: "flex", justifyContent: "space-between", alignItems: "baseline",
      }}>
        <span style={{
          fontWeight: 700, color: theme.colors.text.primary,
          fontFamily: theme.fonts.almanac, letterSpacing: 0.3,
        }}>{title}</span>
        <span style={{ color: theme.colors.brand.accent, fontWeight: 700 }}>
          {fmt1(lineup.totalPoints)}
        </span>
      </div>
      <div>
        {(lineup.starters || []).map((p, i) => (
          <PlayerRow key={`${p.playerId}_${i}`} player={p} />
        ))}
      </div>
      {(lineup.bench || []).length > 0 && (
        <div style={{ borderTop: `1px solid ${theme.colors.border.default}` }}>
          <button
            type="button"
            onClick={onToggleBench}
            style={{
              width: "100%", textAlign: "left",
              background: "transparent", border: "none",
              padding: "8px 12px",
              color: theme.colors.text.muted, fontSize: 11,
              letterSpacing: 1.2, textTransform: "uppercase",
              fontWeight: 700, cursor: "pointer",
              fontFamily: theme.fonts.almanac,
            }}
          >{benchOpen ? "▾" : "▸"} Bench ({lineup.bench.length})</button>
          {benchOpen && lineup.bench.map((p, i) => (
            <PlayerRow key={`b_${p.playerId}_${i}`} player={p} faded />
          ))}
        </div>
      )}
    </div>
  );
}

function PlayerRow({ player, faded = false }) {
  const tc = teamColor(player.nflTeam) || DEFAULT_TEAM_COLOR;
  return (
    <div style={{
      display: "grid",
      gridTemplateColumns: "44px 1fr 46px 56px",
      gap: 8,
      alignItems: "center",
      padding: "6px 12px",
      borderBottom: "1px solid rgba(255,255,255,0.03)",
      opacity: faded ? 0.55 : 1,
    }}>
      <span style={{
        color: theme.colors.text.muted, fontSize: 11,
        fontWeight: 700, letterSpacing: 0.6,
      }}>{player.position || "—"}</span>
      <span style={{
        color: theme.colors.text.primary, fontWeight: 600,
        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
      }}>{abbreviateName(player.name)}</span>
      <span style={{
        background: tc.bg, color: tc.fg,
        fontSize: 10, fontWeight: 700, letterSpacing: 0.5,
        padding: "2px 6px", borderRadius: 4, textAlign: "center",
      }}>{player.nflTeam || "—"}</span>
      <span style={{
        textAlign: "right", color: theme.colors.text.primary,
        fontWeight: 600,
      }}>{fmt1(player.points)}</span>
    </div>
  );
}

// "Christian McCaffrey" -> "C. McCaffrey". Single-name players (DEF entries
// like "49ers") pass through unchanged.
function abbreviateName(name) {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/);
  if (parts.length < 2) return name;
  const first = parts[0];
  const last = parts.slice(1).join(" ");
  return `${first[0]}. ${last}`;
}

function fmt1(n) { return n == null ? "—" : Number(n).toFixed(1); }
